import * as graphics from "@client/app/graphics";
import {SelectChevron} from "@client/components/Chrome";
import {Segmented} from "@client/components/Segmented";
import * as dates from "@client/dates";
import * as auth from "@client/features/auth";
import {useSoftwareKeyboard} from "@client/features/keyboard";
import * as macros from "@client/features/macros";
import * as hardware from "@client/hardware";
import * as router from "@client/router";
import {AnimatedButton, StopWastingTime} from "@client/scenes/PlanOnboarding";
import * as analytics from "@client/shared/analytics";
import {useAppDispatch, useAppSelector} from "@client/store";
import {ArrowRightIcon, ChevronLeftIcon} from "@heroicons/react/24/outline";
import NumberFlow from "@number-flow/react";
import {classNames, server} from "@reactivated";
import {motion} from "motion/react";
// The sign-up flow at /register/ and the macro setup it is built from, which
// registered users re-run at /macros/. Where the routes resolve the demo gate
// off production, ?step=<step>&demo=1 opens any screen with filled-in answers
// for screenshots. Only components leave this file so an edit hot-swaps the
// wizard in place; the route bindings live in routes.tsx.
import React from "react";
import {match} from "ts-pattern";

const GRADIENT =
    "linear-gradient(175deg, oklch(0.96 0.024 45), oklch(0.958 0.020 60) 22%, oklch(0.958 0.020 320) 48%, oklch(0.94 0.03 278))";

const HEADLINE = "mb-1.5 text-[28px] leading-[1.3] font-light text-stone-900";
const LEDE = "mb-5 text-[14px] leading-relaxed font-light text-stone-600";
const GLASS = "bg-white/65 backdrop-blur-xl shadow-[0_5px_20px_rgba(28,25,23,.06)]";
const GLASS_SELECTED =
    "bg-[#f8f7fe] ring-[1.5px] ring-[#c6c1f6] shadow-[0_5px_20px_rgba(85,70,229,.14)]";
const CONTROL =
    "block h-14 w-full rounded-[14px] border border-stone-200 bg-white px-4 text-[15px] text-stone-900 placeholder:text-stone-400 focus:border-[#c6c1f6] focus:ring-2 focus:ring-[#c6c1f6]/60 focus:outline-none";

type Step =
    | "start"
    | "about"
    | "body"
    | "activity"
    | "goal"
    | "macros"
    | "email"
    | "email_exists"
    | "link_sent";

const DATA_STEPS: Step[] = ["about", "body", "activity", "goal", "macros", "email"];

type Mode =
    | {kind: "register"}
    | {kind: "recalculate"; profile: server.core.rpc.Profile.output};

const Shell = ({
    children,
    onBack,
}: {
    children: React.ReactNode;
    onBack: (() => void) | null;
}) => (
    <div className="fixed inset-0 flex flex-col" style={{background: GRADIENT}}>
        <div className="pt-safe-or-padding pb-safe-or-padding relative mx-auto flex size-full max-w-md flex-col overflow-hidden px-5">
            <div className="relative z-10 mt-0.5 mb-3.5 flex flex-none items-center justify-center">
                {onBack != null && (
                    <button
                        type="button"
                        aria-label="Back"
                        onClick={() => {
                            void hardware.haptic();
                            onBack();
                        }}
                        className="absolute top-1/2 left-0 flex size-9 -translate-y-1/2 items-center justify-center rounded-full text-stone-600"
                    >
                        <ChevronLeftIcon className="size-6" />
                    </button>
                )}
                <graphics.Logo className="h-7 w-auto" />
            </div>
            {children}
        </div>
    </div>
);

const StepDots = ({active}: {active: number | null}) => (
    <div className="flex h-4 flex-none items-center justify-center gap-1.5">
        {active != null &&
            DATA_STEPS.map((step, index) => (
                <motion.i
                    key={step}
                    layout
                    className={classNames(
                        "h-1.5 rounded-full",
                        index === active ? "w-4 bg-[#5546e5]" : "w-1.5 bg-[#d6d3d1]",
                    )}
                    transition={{type: "spring", stiffness: 500, damping: 35}}
                />
            ))}
    </div>
);

type NextAction = {
    label: string;
    onClick: () => void | Promise<void>;
    disabled?: boolean;
    isSubmitting?: boolean;
};

type GhostAction = {label: string; onClick: () => void};

// One screen's pieces. The body scrolls; next, ghost and footer sit in the
// pinned bar above the step dots, like the plan onboarding.
type Screen = {
    body: React.ReactNode;
    next: NextAction | null;
    ghost?: GhostAction;
    footer?: React.ReactNode;
    // The body stretches to the full height instead of centering.
    fill?: boolean;
};

// Prose is hand-cut: every line is written out and never auto-wraps.
const LINE = "block whitespace-nowrap";

const Lines = ({lines}: {lines: string[]}) => (
    <>
        {lines.map((line) => (
            <span key={line} className={LINE}>
                {line}
            </span>
        ))}
    </>
);

const Ghost = ({label, onClick}: {label: string; onClick: () => void}) => (
    <button
        type="button"
        onClick={() => {
            void hardware.haptic();
            onClick();
        }}
        className="block w-full text-[15px] font-normal text-stone-600"
    >
        {label}
    </button>
);

const Label = ({
    children,
    htmlFor,
    id,
}: {
    children: React.ReactNode;
    htmlFor?: string;
    id?: string;
}) => (
    <label
        htmlFor={htmlFor}
        id={id}
        className="mb-1.5 block text-left text-[14px] font-medium text-stone-700"
    >
        {children}
    </label>
);

const Choice = ({
    selected,
    title,
    hint,
    onClick,
}: {
    selected: boolean;
    title: string;
    hint: string[];
    onClick: () => void;
}) => (
    <button
        type="button"
        role="radio"
        aria-checked={selected}
        onClick={() => {
            void hardware.haptic();
            onClick();
        }}
        className={classNames(
            "flex h-[84px] w-full items-center gap-3 rounded-[14px] px-4 text-left transition-shadow",
            selected ? GLASS_SELECTED : GLASS,
        )}
    >
        <span className="flex-1">
            <span className="block text-[16px] font-normal text-stone-900">
                {title}
            </span>
            <span className="block text-[13.5px] text-stone-500">
                <Lines lines={hint} />
            </span>
        </span>
        <span
            className={classNames(
                "flex size-6 flex-none items-center justify-center rounded-full",
                selected ? "bg-[#5546e5]" : "border border-stone-300 bg-white",
            )}
        >
            {selected && (
                <svg viewBox="0 0 12 12" className="size-3.5">
                    <path
                        d="M2.8 6.4l2.2 2.2L9.4 3.8"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            )}
        </span>
    </button>
);

const SelectControl = <T extends string>({
    id,
    value,
    options,
    onChange,
}: {
    id: string;
    value: T | null;
    options: readonly (readonly [T | null, string])[];
    onChange: (value: T | null) => void;
}) => (
    <span className="relative block min-w-0 flex-1">
        <select
            id={id}
            aria-label={options[0]?.[1]}
            value={value ?? ""}
            onChange={(event) =>
                onChange(event.target.value === "" ? null : (event.target.value as T))
            }
            className={classNames(
                CONTROL,
                "appearance-none pr-9",
                value == null && "text-stone-400",
            )}
        >
            {options.map(([optionValue, label]) => (
                <option key={optionValue ?? ""} value={optionValue ?? ""}>
                    {label}
                </option>
            ))}
        </select>
        <SelectChevron className="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2" />
    </span>
);

const UnitInput = ({
    id,
    value,
    unit,
    placeholder,
    onChange,
    onEnter,
}: {
    id: string;
    value: string;
    unit: string;
    placeholder: string;
    onChange: (value: string) => void;
    onEnter?: () => void;
}) => (
    <span className="relative block flex-1">
        <input
            id={id}
            type="text"
            inputMode="decimal"
            enterKeyHint="done"
            placeholder={placeholder}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
                if (event.key === "Enter") {
                    event.currentTarget.blur();
                    onEnter?.();
                }
            }}
            className={classNames(CONTROL, "pr-12")}
        />
        <span className="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-[15px] font-light text-stone-700">
            {unit}
        </span>
    </span>
);

const Accent = ({children}: {children: React.ReactNode}) => (
    <b className="font-light text-[#5546e5]">{children}</b>
);

const Strong = ({children}: {children: React.ReactNode}) => (
    <b className="font-normal text-stone-900">{children}</b>
);

const MacrosProse = ({value}: {value: macros.Macros}) => (
    <div className="flex h-full flex-col justify-between py-6 text-center font-extralight text-stone-900">
        <div className="text-[40px] leading-[1.1]">
            <span className={LINE}>
                Eat{" "}
                <Accent>
                    <NumberFlow value={value.calories} />
                </Accent>{" "}
                calories
            </span>
            <span className={LINE}>per day</span>
        </div>
        <div className="flex flex-1 flex-col justify-around pt-6 text-[27px] leading-tight text-stone-800">
            <p className={LINE}>
                Get{" "}
                <Accent>
                    <NumberFlow value={value.protein} />
                </Accent>{" "}
                grams of <Strong>protein</Strong>
            </p>
            <p className={LINE}>
                Aim for{" "}
                <Accent>
                    <NumberFlow value={value.fat} />
                </Accent>{" "}
                grams of <Strong>fat</Strong>
            </p>
            <p className={LINE}>
                And{" "}
                <Accent>
                    <NumberFlow value={value.carbs} />
                </Accent>{" "}
                grams of <Strong>carbs</Strong>
            </p>
            <p>
                <span className={LINE}>
                    Eat <Strong>whole foods</Strong> and
                </span>
                <span className={LINE}>
                    <Strong>avoid</Strong> processed foods.
                </span>
            </p>
        </div>
    </div>
);

const params = () =>
    typeof window === "undefined" ? null : new URLSearchParams(window.location.search);

const Wizard = ({mode, demoAllowed}: {mode: Mode; demoAllowed: boolean}) => {
    const dispatch = useAppDispatch();
    const platform = useAppSelector((state) => state.boot.platform);
    const subscriptionDollarsPerMonth = platform === "ios" ? "4.99" : "5";
    const [query] = React.useState(params);
    const rig = demoAllowed ? query : null;
    const demo = rig?.get("demo") === "1";
    const {
        body,
        activity,
        setActivity,
        goal,
        setGoal,
        macros: computed,
    } = macros.useDetails(mode.kind === "recalculate" ? mode.profile : null, demo);
    const {fields} = body.form;
    const [step, setStep] = React.useState<Step>(
        (rig?.get("step") as Step | null) ??
            (mode.kind === "register"
                ? "start"
                : computed == null
                  ? "about"
                  : "macros"),
    );
    const [email, setEmail] = React.useState(rig?.get("email") ?? "");
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const keyboard = useSoftwareKeyboard();

    const goto = (next: Step) => {
        setError(null);
        setStep(next);
    };

    const finish = async (profile: server.core.rpc.Profile.output) => {
        await dispatch(auth.postLogin(profile, true));
        void router.push("today.journal", {});
    };

    // Declining is a choice, not a side effect of an incomplete form: only
    // the ghost skips, and finishing the macros step takes it back.
    const [skipped, setSkipped] = React.useState(false);
    const setup = () =>
        skipped ? null : macros.toSetup(body, activity, goal, computed);

    const register = async () => {
        const trimmed = email.trim();
        const {platform} = await hardware.getAppInfo();
        const tag = await hardware.getTag();
        void analytics.track("REGISTERING", {registration_method: "onboarding"});
        const result = await server.journal.onboarding.onboarding_registration({
            registration: {
                email: trimmed,
                with_notification: false,
                invite_code: "onboarding",
                recipe_uuid: null,
                source: tag ?? "onboarding",
                platform,
                timezone: dates.deviceTimezone(),
            },
            setup: setup(),
        });
        if (result.type !== "success") {
            setError("Could not sign you up. Try again.");
            return;
        }
        void analytics.registered(result.data.user.tracking_key, "onboarding", "trial");
        await finish(result.data);
    };

    // An existing account that check_email or the login link just signed in:
    // apply the answers to it before heading to the journal.
    const adopt = async (profile: server.core.rpc.Profile.output) => {
        const pending = setup();
        if (pending == null) {
            await finish(profile);
            return;
        }
        const saved = await server.journal.onboarding.save_macros(pending);
        await finish(saved.type === "success" ? saved.data : profile);
    };

    const handleEmail = async () => {
        setIsSubmitting(true);
        setError(null);
        try {
            const result = await server.core.rpc.check_email(email.trim());
            if (result.type !== "success") {
                setError("Something went wrong. Try again.");
            } else if (result.data === false) {
                await register();
            } else if (result.data === true) {
                goto("email_exists");
            } else {
                await adopt(result.data);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSignIn = async () => {
        setIsSubmitting(true);
        setError(null);
        try {
            const result = await server.core.rpc.request_login_link(email.trim());
            if (result.type !== "success") {
                setError("Something went wrong. Try again.");
            } else if (typeof result.data === "boolean") {
                goto("link_sent");
            } else {
                await adopt(result.data);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSave = async () => {
        const pending = setup();
        if (pending == null) {
            return;
        }
        setIsSubmitting(true);
        setError(null);
        try {
            const result = await server.journal.onboarding.save_macros(pending);
            if (result.type !== "success") {
                setError("Could not save. Try again.");
                return;
            }
            dispatch({type: "SET_PROFILE", profile: result.data});
            void router.push("today.journal", {});
        } finally {
            setIsSubmitting(false);
        }
    };

    const previous = (): Step | null =>
        match(step)
            .with("start", () => null)
            .with("about", () => (mode.kind === "register" ? "start" : null))
            .with("body", () => "about" as const)
            .with("activity", () => "body" as const)
            .with("goal", () => "activity" as const)
            .with("macros", () => "goal" as const)
            .with("email", () => (computed == null ? "about" : "macros") as Step)
            .with("email_exists", "link_sent", () => "email" as const)
            .exhaustive();

    const back = previous();
    const onBack =
        back != null
            ? () => goto(back)
            : mode.kind === "recalculate"
              ? () => router.push("today.journal", {})
              : null;

    const emailValid = /.+@.+\..+/.test(email.trim());
    const isLast = mode.kind === "recalculate";

    const screen: Screen = match(step)
        .with("start", () => ({
            body: <StopWastingTime className="mx-auto h-[58dvh] w-full" />,
            next: {label: "Get started", onClick: () => goto("about")},
            footer: (
                <p className="text-[13.5px] text-stone-600">
                    Returning user?{" "}
                    <router.Link
                        route="login.index"
                        params={{}}
                        className="font-semibold text-[#5546e5]"
                    >
                        Login
                    </router.Link>
                </p>
            ),
        }))
        .with("about", () => ({
            body: (
                <>
                    <h3 className={HEADLINE}>Your details</h3>
                    <p className={LEDE}>
                        <Lines lines={["Tell us a little about yourself."]} />
                    </p>
                    <div className="space-y-4 text-left">
                        <div>
                            <Label id="wizard-gender">What's your gender?</Label>
                            <Segmented
                                labelledBy="wizard-gender"
                                value={fields.gender.value}
                                options={[
                                    {value: "MALE", label: "Male"},
                                    {value: "FEMALE", label: "Female"},
                                ]}
                                onChange={fields.gender.onChange}
                            />
                        </div>
                        <div>
                            <Label id="wizard-birthday">When were you born?</Label>
                            <div
                                role="group"
                                aria-labelledby="wizard-birthday"
                                className="flex gap-2.5"
                            >
                                <SelectControl
                                    id="wizard-birth-month"
                                    value={fields.birth_month.value}
                                    options={fields.birth_month.options}
                                    onChange={fields.birth_month.onChange}
                                />
                                <SelectControl
                                    id="wizard-birth-day"
                                    value={fields.birth_day.value}
                                    options={fields.birth_day.options}
                                    onChange={fields.birth_day.onChange}
                                />
                                <SelectControl
                                    id="wizard-birth-year"
                                    value={fields.birth_year.value}
                                    options={fields.birth_year.options}
                                    onChange={fields.birth_year.onChange}
                                />
                            </div>
                        </div>
                    </div>
                </>
            ),
            next: {
                label: "Continue",
                disabled: !macros.isAboutComplete(body),
                onClick: () => goto("body"),
            },
            ghost:
                mode.kind === "register"
                    ? {
                          label: "I just want to track",
                          onClick: () => {
                              setSkipped(true);
                              goto("email");
                          },
                      }
                    : undefined,
        }))
        .with("body", () => ({
            body: (
                <>
                    <h3 className={HEADLINE}>Height and weight</h3>
                    <p className={LEDE}>
                        <Lines lines={["Use whichever units you think in."]} />
                    </p>
                    <div className="space-y-4 text-left">
                        <Segmented
                            value={fields.units.value}
                            options={[
                                {value: "IMPERIAL", label: "Imperial"},
                                {value: "METRIC", label: "Metric"},
                            ]}
                            onChange={fields.units.onChange}
                        />
                        <div>
                            <Label
                                id="wizard-height"
                                htmlFor={
                                    fields.units.value === "IMPERIAL"
                                        ? undefined
                                        : "wizard-height-cm"
                                }
                            >
                                How tall are you?
                            </Label>
                            {fields.units.value === "IMPERIAL" ? (
                                <div
                                    role="group"
                                    aria-labelledby="wizard-height"
                                    className="flex gap-2.5"
                                >
                                    <SelectControl
                                        id="wizard-height-feet"
                                        value={fields.feet.value}
                                        options={fields.feet.options}
                                        onChange={fields.feet.onChange}
                                    />
                                    <SelectControl
                                        id="wizard-height-inches"
                                        value={fields.inches.value}
                                        options={fields.inches.options}
                                        onChange={fields.inches.onChange}
                                    />
                                </div>
                            ) : (
                                <UnitInput
                                    id="wizard-height-cm"
                                    value={fields.heightCm.value ?? ""}
                                    unit="cm"
                                    placeholder="175"
                                    onChange={fields.heightCm.onChange}
                                />
                            )}
                        </div>
                        <div>
                            <Label htmlFor="wizard-weight">
                                What's your approximate weight?
                            </Label>
                            <UnitInput
                                id="wizard-weight"
                                value={
                                    (fields.units.value === "IMPERIAL"
                                        ? fields.weightLb.value
                                        : fields.weightKg.value) ?? ""
                                }
                                unit={fields.units.value === "IMPERIAL" ? "lb" : "kg"}
                                placeholder={
                                    fields.units.value === "IMPERIAL" ? "165" : "75"
                                }
                                onChange={(weight) =>
                                    (fields.units.value === "IMPERIAL"
                                        ? fields.weightLb
                                        : fields.weightKg
                                    ).onChange(weight)
                                }
                                onEnter={() => {
                                    if (macros.isBodyComplete(body)) {
                                        goto("activity");
                                    }
                                }}
                            />
                        </div>
                    </div>
                </>
            ),
            next: {
                label: "Continue",
                disabled: !macros.isBodyComplete(body),
                onClick: () => goto("activity"),
            },
        }))
        .with("activity", () => ({
            body: (
                <>
                    <h3 id="wizard-activity" className={HEADLINE}>
                        How active are you?
                    </h3>
                    <p className={LEDE}>
                        <span className={LINE}>Do not factor in your workouts.</span>
                        <span className={LINE}>
                            You should probably pick{" "}
                            <b className="font-medium text-stone-800">Sedentary</b>.
                        </span>
                    </p>
                    <div
                        role="radiogroup"
                        aria-labelledby="wizard-activity"
                        className="space-y-2.5"
                    >
                        {macros.ACTIVITY_OPTIONS.map((option) => (
                            <Choice
                                key={option.value}
                                selected={activity === option.value}
                                title={option.title}
                                hint={option.hint}
                                onClick={() => setActivity(option.value)}
                            />
                        ))}
                    </div>
                </>
            ),
            next: {
                label: "Continue",
                disabled: activity == null,
                onClick: () => goto("goal"),
            },
        }))
        .with("goal", () => ({
            body: (
                <>
                    <h3 id="wizard-goal" className={HEADLINE}>
                        What's your goal?
                    </h3>
                    <p className={LEDE}>
                        <Lines lines={["Helps us calculate your caloric needs."]} />
                    </p>
                    <div
                        role="radiogroup"
                        aria-labelledby="wizard-goal"
                        className="space-y-2.5"
                    >
                        {macros.GOAL_OPTIONS.map((option) => (
                            <Choice
                                key={option.value}
                                selected={goal === option.value}
                                title={option.title}
                                hint={option.hint}
                                onClick={() => setGoal(option.value)}
                            />
                        ))}
                    </div>
                </>
            ),
            next: {
                label: "See my macros",
                disabled: goal == null,
                onClick: () => goto("macros"),
            },
        }))
        .with("macros", () =>
            computed == null
                ? {
                      body: (
                          <>
                              <h3 className={HEADLINE}>Your details</h3>
                              <p className={LEDE}>
                                  <Lines lines={["Please fill out all fields."]} />
                              </p>
                          </>
                      ),
                      next: {label: "Go back", onClick: () => goto("about")},
                  }
                : {
                      body: <MacrosProse value={computed} />,
                      fill: true,
                      next: {
                          label: isLast ? "Save" : "Looks good",
                          isSubmitting,
                          disabled: isSubmitting,
                          onClick: isLast
                              ? handleSave
                              : () => {
                                    setSkipped(false);
                                    goto("email");
                                },
                      },
                      ghost: {label: "Start over", onClick: () => goto("about")},
                  },
        )
        .with("email", () => ({
            body: (
                <>
                    <h3 className={HEADLINE}>Create your account</h3>
                    <p className={LEDE}>
                        <span className={LINE}>
                            <graphics.Logo className="inline h-[0.75em] w-auto overflow-visible align-baseline" />{" "}
                            tracks nutrition, workouts and habits.
                        </span>
                        <span className={LINE}>
                            It's free for 7 days. Then, it's just{" "}
                            <span className="font-medium text-stone-800">
                                ${subscriptionDollarsPerMonth}
                            </span>{" "}
                            a month.
                        </span>
                    </p>
                    <input
                        id="wizard-email"
                        aria-label="Email"
                        type="email"
                        inputMode="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        enterKeyHint="go"
                        placeholder="Email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" && emailValid && !isSubmitting) {
                                event.currentTarget.blur();
                                void handleEmail();
                            }
                        }}
                        className={CONTROL}
                    />
                </>
            ),
            next: {
                label: "Create account",
                disabled: !emailValid || isSubmitting,
                isSubmitting,
                onClick: handleEmail,
            },
            footer: (
                <a
                    href="https://www.joyapp.com/terms-of-service/"
                    className={classNames(
                        LINE,
                        "text-[13.5px] text-stone-500 underline",
                    )}
                >
                    Terms and conditions
                </a>
            ),
        }))
        .with("email_exists", () => ({
            body: (
                <>
                    <h3 className={HEADLINE}>Welcome back</h3>
                    <p className={LEDE}>
                        <span className={LINE}>
                            This email already has a{" "}
                            <graphics.Logo className="inline h-[0.75em] w-auto overflow-visible align-baseline" />{" "}
                            account.
                        </span>
                        <span className={LINE}>
                            Sign in and we'll save your plan to it.
                        </span>
                    </p>
                    <input
                        aria-label="Email"
                        type="email"
                        value={email}
                        disabled
                        className={CONTROL}
                    />
                </>
            ),
            next: {
                label: "Sign in",
                disabled: isSubmitting,
                isSubmitting,
                onClick: handleSignIn,
            },
            ghost: {label: "Use a different email", onClick: () => goto("email")},
        }))
        .with("link_sent", () => ({
            body: (
                <>
                    <h3 className={HEADLINE}>Check your email</h3>
                    <p className={LEDE}>
                        <span className="block">
                            We sent a sign-in link to{" "}
                            <span className="font-medium text-stone-800">{email}</span>.
                        </span>
                        <span className={LINE}>
                            It signs you in and takes you to your journal.
                        </span>
                    </p>
                </>
            ),
            next: null,
            ghost: {label: "Use a different email", onClick: () => goto("email")},
        }))
        .exhaustive();

    const dot = match(step)
        .with("start", () => null)
        .with("about", () => 0)
        .with("body", () => 1)
        .with("activity", () => 2)
        .with("goal", () => 3)
        .with("macros", () => 4)
        .with("email", "email_exists", "link_sent", () => 5)
        .exhaustive();

    return (
        <Shell onBack={onBack}>
            <div className="-mx-1 flex min-h-0 flex-1 flex-col overflow-y-auto px-1">
                <motion.div
                    key={step}
                    initial={{opacity: 0, y: 12}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.25}}
                    className={classNames(
                        "py-4 text-center",
                        screen.fill === true ? "flex min-h-full flex-col" : "my-auto",
                    )}
                >
                    {error != null && (
                        <span className="mb-2 block text-[12.5px] font-semibold text-[#d92d20]">
                            {error}
                        </span>
                    )}
                    {screen.body}
                </motion.div>
            </div>
            <div className="relative z-10 flex flex-none flex-col items-center gap-3.5 pt-3 text-center">
                {!keyboard.open && screen.ghost != null && (
                    <Ghost label={screen.ghost.label} onClick={screen.ghost.onClick} />
                )}
                {!keyboard.open && screen.footer}
                {screen.next != null && (
                    <div className="flex justify-center">
                        <AnimatedButton
                            label={screen.next.label}
                            icon={ArrowRightIcon}
                            iconPosition="right"
                            animateIcon
                            onClick={screen.next.onClick}
                            isSubmitting={screen.next.isSubmitting ?? false}
                            disabled={screen.next.disabled ?? false}
                        />
                    </div>
                )}
                {!keyboard.open && <StepDots active={dot} />}
            </div>
        </Shell>
    );
};

export const RegisterWizard = (props: {demoAllowed: boolean}) => (
    <Wizard mode={{kind: "register"}} demoAllowed={props.demoAllowed} />
);

export const MacrosWizard = (props: {
    profile: server.core.rpc.Profile.output;
    demoAllowed: boolean;
}) => (
    <Wizard
        mode={{kind: "recalculate", profile: props.profile}}
        demoAllowed={props.demoAllowed}
    />
);
